import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  Box,
  Grid
} from '@mui/material';
import { WalteroButton, WalteroTypography } from '../waltero';
import { subscriptionService } from '../../services/subscriptionService';

const EditSubscriptionTypeModal = ({ open, onClose, onSuccess, subscriptionType }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    durationDays: '',
    basePrice: '',
    gracePeriodDays: '',
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Update form data when subscriptionType prop changes
  useEffect(() => {
    console.log('🔍 EditModal - subscriptionType prop:', subscriptionType);
    console.log('🔍 EditModal - open prop:', open);
    
    if (subscriptionType && open) {
      console.log('🔍 EditModal - Setting form data with:', {
        name: subscriptionType.name || '',
        description: subscriptionType.description || '',
        durationDays: subscriptionType.durationDays?.toString() || '',
        basePrice: subscriptionType.basePrice?.toString() || '',
        gracePeriodDays: subscriptionType.gracePeriodDays?.toString() || '',
        isActive: subscriptionType.isActive !== false
      });
      
      setFormData({
        name: subscriptionType.name || '',
        description: subscriptionType.description || '',
        durationDays: subscriptionType.durationDays?.toString() || '',
        basePrice: subscriptionType.basePrice?.toString() || '',
        gracePeriodDays: subscriptionType.gracePeriodDays?.toString() || '',
        isActive: subscriptionType.isActive !== false
      });
    }
  }, [subscriptionType, open]);

  const handleChange = (field) => (event) => {
    const value = field === 'isActive' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      // Prepare data for submission
      const updateData = {
        ...formData,
        durationDays: parseInt(formData.durationDays) || 0,
        basePrice: formData.basePrice ? parseFloat(formData.basePrice) : null,
        gracePeriodDays: formData.gracePeriodDays === '' ? 0 : Math.max(0, parseInt(formData.gracePeriodDays, 10) || 0)
      };

      // Validate required fields
      if (!updateData.name.trim()) {
        throw new Error('Name is required');
      }
      if (!updateData.durationDays || updateData.durationDays <= 0) {
        throw new Error('Duration must be greater than 0 days');
      }

      await subscriptionService.updateSubscriptionType(subscriptionType.id, updateData);
      
      onSuccess?.();
      onClose();
      
    } catch (err) {
      console.error('Error updating subscription type:', err);
      setError(err.message || 'Failed to update subscription type');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  if (!subscriptionType) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <WalteroTypography 
          variant="h5" 
          sx={{ 
            color: 'waltero.primary.coreBlue',
            fontWeight: 600 
          }}
        >
          Edit Subscription Type
        </WalteroTypography>
        <WalteroTypography 
          variant="body2" 
          sx={{ color: 'text.secondary', mt: 1 }}
        >
          Editing: {subscriptionType.name}
        </WalteroTypography>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name *"
                value={formData.name}
                onChange={handleChange('name')}
                disabled={loading}
                placeholder="e.g., Basic Plan, Premium Plan"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleChange('description')}
                disabled={loading}
                multiline
                rows={2}
                placeholder="Brief description of this subscription type"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Duration (Days) *"
                type="number"
                value={formData.durationDays}
                onChange={handleChange('durationDays')}
                disabled={loading}
                inputProps={{ min: 1 }}
                placeholder="e.g., 30, 365"
                helperText="Number of days this subscription lasts"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Base Price (per sensor / month)"
                type="number"
                value={formData.basePrice}
                onChange={handleChange('basePrice')}
                disabled={loading}
                inputProps={{ min: 0, step: 0.01 }}
                placeholder="e.g., 29.99"
                helperText="Charge applied per sensor each month. Leave empty for custom pricing."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Grace Period (Days)"
                type="number"
                value={formData.gracePeriodDays}
                onChange={handleChange('gracePeriodDays')}
                disabled={loading}
                inputProps={{ min: 0 }}
                placeholder="e.g., 14"
                helperText="Days before billing starts after activation"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box sx={{ pt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleChange('isActive')}
                      disabled={loading}
                      color="primary"
                    />
                  }
                  label="Active"
                />
                <WalteroTypography 
                  variant="caption" 
                  sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}
                >
                  Active subscription types can be assigned to organizations
                </WalteroTypography>
              </Box>
            </Grid>

            {/* Warning about existing assignments */}
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>Note:</strong> Changes to this subscription type will affect all organizations that use it. 
                Organization-specific overrides will still apply.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <WalteroButton 
            onClick={handleClose}
            disabled={loading}
            variant="outlined"
          >
            CANCEL
          </WalteroButton>
          <WalteroButton 
            type="submit"
            disabled={loading}
            variant="contained"
          >
            {loading ? 'UPDATING...' : 'UPDATE TYPE'}
          </WalteroButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditSubscriptionTypeModal;
